import { themes } from 'kb-shared';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as SortASCSvg } from '../../../assets/icons/sort-ascending.svg';
import { ReactComponent as SortDESCSvg } from '../../../assets/icons/sort-descending.svg';
import { showErrorToast } from '../../../utilities/notificationUtils';
import { deleteDocAsync } from '../lib/DocumentManager';
import { FoundFile } from '../types';
import ViewButton from './ViewButton';
import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';

const { colors, fonts, breakpoints } = themes;

const destroyPatientUploadMutation = gql`
  mutation destroyPatientUpload($fileName: String!) {
    destroyPatientUpload(fileName: $fileName) {
      patientUpload {
        id
      }
    }
  }
`;
interface DestroyPatientUploadResponse {
  id: string;
}
interface DestroyPatientUploadVariables {
  fileName: string;
}

enum SORT_DIRECTION {
  ASC = 1,
  DESC = -1
}

type Props = {
  items: FoundFile[];
  properties?: Array<string>;
  hasHeader?: boolean;
  onDelete?: () => void;
};

type SortedBy = {
  property: string;
  direction: SORT_DIRECTION;
};

type SortableHeaderProps = { name: string; children: any; onSort: Function };

const DocumentsList = ({ items, properties = [], hasHeader = false, onDelete }: Props) => {
  const [documents, setDocuments] = useState([]);
  const [sortedBy, setSortedBy] = useState<SortedBy>({
    property: 'date',
    direction: SORT_DIRECTION.DESC
  });

  const [destroyPatientUpload] = useMutation<
    DestroyPatientUploadResponse,
    DestroyPatientUploadVariables
  >(destroyPatientUploadMutation);

  const toggleSortBy = (property: string) => {
    setSortedBy({
      property,
      direction:
        sortedBy.property === property && sortedBy.direction === SORT_DIRECTION.ASC
          ? SORT_DIRECTION.DESC
          : SORT_DIRECTION.ASC
    });
  };

  const extraProperties = properties.length;
  const className = `properties-${extraProperties}`;

  const SortableHeader = ({ name, children, onSort }: SortableHeaderProps) => {
    const isActive = sortedBy.property === name;
    const className = `${isActive ? 'active' : ''}`;
    return (
      <Header key={`header-${name}`} onClick={() => onSort(name)} className={className}>
        {children}{' '}
        {isActive && sortedBy.direction === SORT_DIRECTION.ASC && (
          <SortASCSvg style={{ marginLeft: 10 }} />
        )}
        {isActive && sortedBy.direction === SORT_DIRECTION.DESC && (
          <SortDESCSvg style={{ marginLeft: 10 }} />
        )}
      </Header>
    );
  };

  const renderHeaders = () => (
    <Headers key="document-header" className={className}>
      <SortableHeader name="fileName" onSort={toggleSortBy}>
        Name
      </SortableHeader>
      {properties.includes('type') && (
        <SortableHeader name="type" onSort={toggleSortBy}>
          Type
        </SortableHeader>
      )}
      {properties.includes('date') && (
        <SortableHeader name="date" onSort={toggleSortBy}>
          Date
        </SortableHeader>
      )}
      <Header />
    </Headers>
  );

  const sortBy = (
    properties: Array<string>,
    arr: Array<any>,
    direction = SORT_DIRECTION.ASC,
    compareFn: Function
  ) =>
    arr.sort((a, b) => {
      let i = 0,
        result = 0;
      while (i < properties.length && result === 0) {
        result = compareFn(a[properties[i]], b[properties[i]], properties[i]) * direction;
        i++;
      }
      return result;
    });

  const compareDocuments = (a: any, b: any, property: string): number => {
    switch (property) {
      case 'type':
      case 'fileName':
        return a.localeCompare(b);
      case 'date':
        return moment(a).isBefore(b) ? (moment(a).isSame(b) ? 0 : -1) : 1;
      default:
        return a - b;
    }
  };

  const openRawHtml = (title: string, data: string) => {
    const win = window.open('', title, '');
    // @ts-ignore
    win.document.write(data);
  };

  useEffect(() => {
    // @ts-ignore
    const { mappedItems } = items.reduce(
      // @ts-ignore
      (acc, item) => {
        // @ts-ignore
        let copyNumber = 0;
        const copies = { ...acc.copies };
        // @ts-ignore
        if (item.fileName in copies) {
          // @ts-ignore
          copyNumber = acc.copies[item.fileName];
          // @ts-ignore
          copies[item.fileName] = copyNumber + 1;
        } else {
          // @ts-ignore
          copies[item.fileName] = 1;
        }

        return {
          mappedItems: [
            ...acc.mappedItems,
            { ...item, fileName: `${item.fileName}${copyNumber > 0 ? ` (${copyNumber})` : ''}` }
          ],
          copies
        };
      },
      { mappedItems: [], copies: {} }
    );
    const newDocuments = sortBy(
      [sortedBy.property],
      mappedItems,
      sortedBy.direction,
      compareDocuments
    );
    // @ts-ignore
    setDocuments(newDocuments);
  }, [items, sortedBy]);

  return (
    <Container>
      {hasHeader && renderHeaders()}
      {documents.map((item, index) => {
        const { path, fileName, destination, s3Path, type, date } = item;
        const formattedDate = date ? moment(date).format('MM/DD/YYYY') : null;
        return (
          <Row key={`document-${path}-${index}`} className={className}>
            <Name>{fileName}</Name>
            {properties.includes('type') && <Type>{type}</Type>}
            {properties.includes('date') && <Date>{formattedDate}</Date>}
            <ActionsContainer>
              {destination === 'personal' && (
                <Delete
                  onClick={e => {
                    e.preventDefault();
                    deleteDocAsync(s3Path)
                      .then(success => {
                        if (!success) {
                          showErrorToast(
                            "We're sorry something unexpected happened. Please try again."
                          );
                          return;
                        } else {
                          destroyPatientUpload({
                            variables: { fileName }
                          });
                        }
                        if (onDelete) onDelete();
                      })
                      .catch(_ => {
                        showErrorToast(
                          "We're sorry something unexpected happened. Please try again."
                        );
                      });
                  }}
                >
                  Delete
                </Delete>
              )}
              {type === 'HTML' && (
                <LinkHTML onClick={() => openRawHtml(fileName, path)}>
                  <ViewButton />
                </LinkHTML>
              )}
              {type !== 'HTML' && (
                <Link target="_blank" href={path}>
                  <ViewButton />
                </Link>
              )}
            </ActionsContainer>
          </Row>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  margin-bottom: 28px;
  display: grid;
  grid-gap: 2px;
`;

const Row = styled.div`
  background-color: ${colors.KBWhite};
  padding: 8px 15px;

  margin: 0;
  display: grid;
  /* @ts-ignore */
  grid-gap: 10px;

  &:last-of-type {
    margin-bottom: 0px;
  }

  &.properties-0 {
    grid-template-columns: 50% 1fr;
  }
  &.properties-1 {
    grid-template-columns: 50% 1fr 1fr;
  }
  &.properties-2 {
    grid-template-columns: 50% 1fr 1fr 1fr;
  }

  @media (max-width: ${breakpoints.tabletPortrait}) {
    flex-direction: column;
  }
`;

const Headers = styled(Row)`
  background-color: transparent;
`;

const BaseColumn = styled.div`
  ${themes.fonts.FoundersGrotesk};
  border: none;
  font-size: 14px;
  padding: 0 10px;
  display: flex;
  align-items: center;
`;

const Header = styled(BaseColumn)`
  ${themes.fonts.FoundersGrotesk};

  padding-top: 10px;
  cursor: pointer;

  &.active {
    font-weight: bold;
  }
`;

const Name = styled(BaseColumn)`
  ${fonts.DomaineDisplay};
  font-size: 24px;
`;

const Type = styled(BaseColumn)``;

const Date = styled(BaseColumn)``;

const ActionsContainer = styled(BaseColumn)`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    justify-content: space-between;
  }
`;

const Delete = styled.button`
  cursor: pointer;
  text-decoration: underline;
  margin-right: 10px;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    align-self: flex-end;
    padding-left: 0;
  }
`;

const Link = styled.a`
  color: ${colors.KBNight};
  font-size: 18px;
  font-weight: 100;
  text-decoration: none !important;
`;

const LinkHTML = styled.div`
  color: ${colors.KBNight};
  font-size: 18px;
  font-weight: 100;
  text-decoration: none !important;
  cursor: pointer;
`;

export default DocumentsList;
